/* Detail-drawer van een lead: timeline, stakeholders, outreach-historiek. */
import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import i18n from "i18next";
import { Link } from "react-router-dom";
import { X, Loader2, Network, ArrowRight, Send, History, Trash2, Building2, Archive, ArchiveRestore, Rocket, Edit3 } from "lucide-react";
import { LinkedInButton } from "@/components/common/LinkedInButton";
import ContactDrawer from "@/components/ContactDrawer";
import ScaliqPushPopover from "@/components/sales/ScaliqPushPopover";
import { AddToCampaignPopover } from "@/components/sales/AddToCampaignPopover";
import { STAGES, SOURCE_STYLE, CHANNEL_ICONS, sourceOf, fmtEur } from "./constants";
import { fireWonConfetti } from "./wonConfetti";

/* ------------------------------------------------------------------ */
/*  Lead detail drawer                                                 */
/* ------------------------------------------------------------------ */
function LeadDetailDrawer({ leadId, initial, onClose, onChanged }) {
    const [lead, setLead] = useState(initial);
    const [messages, setMessages] = useState([]);
    const [audit, setAudit] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState(false);
    const [archiving, setArchiving] = useState(false);
    const [contactRef, setContactRef] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [a, b] = await Promise.all([
                api.get("/leads?include_archived=true"),
                api.get(`/outreach/messages?lead_id=${leadId}`),
            ]);
            const fresh = a.data.find((x) => x.id === leadId);
            if (fresh) setLead(fresh);
            setMessages(b.data || []);
            const ax = await api.get(`/audit?limit=80`);
            setAudit((ax.data || []).filter((e) => e.resource_id === leadId).slice(0, 12));
        } catch (e) { /* silent */ }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [leadId]);

    const move = async (stage) => {
        try {
            await api.put(`/leads/${leadId}`, { stage });
            if (stage === "won") {
                fireWonConfetti();
                toast.success(i18n.t("pipeline_sales.won_toast"));
            } else {
                toast.success(`→ ${stage}`);
            }
            await load(); onChanged?.();
        }
        catch (e) { toast.error(i18n.t("common.failed")); }
    };

    const remove = async () => {
        if (!confirm(i18n.t("pipeline_sales.delete_confirm", { company: lead?.company_name }))) return;
        setDeleting(true);
        try {
            const r = await api.delete(`/leads/${leadId}`);
            toast.success(r.data.outreach_deleted ? i18n.t("pipeline_sales.deleted_with", { count: r.data.outreach_deleted }) : i18n.t("common.deleted"));
            onClose?.();
            onChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("pipeline_sales.delete_failed")); }
        finally { setDeleting(false); }
    };

    const archive = async () => {
        setArchiving(true);
        try {
            await api.post(`/leads/${leadId}/archive`);
            toast.success(i18n.t("pipeline_sales.archived_ok"));
            await load(); onChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("pipeline_sales.archive_failed")); }
        finally { setArchiving(false); }
    };
    const restore = async () => {
        setArchiving(true);
        try {
            await api.post(`/leads/${leadId}/restore`);
            toast.success(i18n.t("pipeline_sales.restored_ok"));
            await load(); onChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("pipeline_sales.restore_failed")); }
        finally { setArchiving(false); }
    };

    if (!lead) return null;
    const src = sourceOf(lead);
    const style = SOURCE_STYLE[src] || SOURCE_STYLE.agent;
    const stakeholders = lead.stakeholders || [];

    return (
        <div className="fixed inset-0 z-50 bg-[var(--jr-bg)] overflow-y-auto"
            onKeyDown={(e) => { if (e.key === "Escape") onClose?.(); }}
            data-testid="lead-detail-fullscreen">
            <div className="w-full max-w-6xl mx-auto"
                data-testid="lead-detail-drawer">
                {/* Prominent back-navigation bar */}
                <div className="sticky top-0 z-20 bg-[var(--jr-bg)]/95 backdrop-blur border-b border-white/8 px-6 py-3">
                    <button onClick={onClose} data-testid="lead-back-btn"
                        className="inline-flex items-center gap-2 text-sm text-[var(--jr-text-2)] hover:text-white transition-colors">
                        <ArrowRight size={14} className="rotate-180" /> Terug naar pipeline
                    </button>
                </div>
                <header className={`p-6 border-b border-white/10 ${style.cardBorder.includes("bg-") ? style.cardBorder.split(" ").find(c => c.startsWith("bg-")) : ""}`}>
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h2 className="font-display text-2xl tracking-tight truncate">{lead.company_name}</h2>
                                <span className={`text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-0.5 rounded ${style.badgeBg} ${style.badgeText} border ${style.badgeBorder}`}>
                                    <style.Icon size={10} className="inline mr-1" /> {style.label}
                                </span>
                                {lead.is_archived && (
                                    <span className="text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-0.5 rounded border border-white/15 bg-white/[0.06] text-[var(--jr-text-2)] inline-flex items-center gap-1"
                                        data-testid={`drawer-archived-${leadId}`}>
                                        <Archive size={10} /> Gearchiveerd
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-[var(--jr-text-2)] mt-1">
                                <span className={lead.contact_name ? "cursor-pointer hover:text-[var(--jr-brand)] hover:underline underline-offset-2 transition-colors" : ""}
                                    onClick={() => lead.contact_name && setContactRef({ kind: "primary", leadId })}
                                    data-testid={`drawer-open-primary-${leadId}`}>
                                    {lead.contact_name || "—"}
                                </span> · {lead.contact_title || "—"}
                            </p>
                            {lead.created_by_name && (
                                <p className="text-[11px] text-[var(--jr-text-3)] mt-0.5">
                                    Toegevoegd door {lead.created_by_name}{lead.manual_source ? ` · ${lead.manual_source}` : ""}
                                </p>
                            )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                            {lead.is_archived ? (
                                <button onClick={restore} disabled={archiving} data-testid={`drawer-restore-${leadId}`}
                                    title="Herstel company"
                                    className="p-1.5 rounded text-[var(--jr-info)] hover:text-white hover:bg-[var(--jr-info)]/10 disabled:opacity-50">
                                    {archiving ? <Loader2 className="animate-spin" size={16} /> : <ArchiveRestore size={16} />}
                                </button>
                            ) : (
                                <button onClick={archive} disabled={archiving} data-testid={`drawer-archive-${leadId}`}
                                    title="Archiveer company (reversibel)"
                                    className="p-1.5 rounded text-[var(--jr-text-3)] hover:text-white hover:bg-white/5 disabled:opacity-50">
                                    {archiving ? <Loader2 className="animate-spin" size={16} /> : <Archive size={16} />}
                                </button>
                            )}
                            <button onClick={remove} disabled={deleting} data-testid={`drawer-delete-${leadId}`}
                                title={i18n.t("pipeline_sales.delete_company_tip")}
                                className="p-1.5 rounded text-[var(--jr-text-3)] hover:text-[var(--jr-error)] hover:bg-[var(--jr-error)]/10 disabled:opacity-50">
                                {deleting ? <Loader2 className="animate-spin" size={16} /> : <Trash2 size={16} />}
                            </button>
                            <button onClick={onClose} className="text-[var(--jr-text-3)] hover:text-white p-1"><X size={18} /></button>
                        </div>
                    </div>

                    {/* Stage selector */}
                    <div className="mt-4 flex flex-wrap gap-1 items-center">
                        {STAGES.map((s) => (
                            <button key={s.id} onClick={() => move(s.id)} data-testid={`drawer-stage-${s.id}`}
                                className={`text-[10px] font-mono uppercase tracking-[0.16em] px-2.5 py-1 rounded-full border transition-colors ${lead.stage === s.id ? "bg-[var(--jr-brand)] text-black border-[var(--jr-brand)]" : "border-white/10 text-[var(--jr-text-2)] hover:bg-white/5"}`}>
                                {s.label}
                            </button>
                        ))}
                        <span className="flex-1" />
                        <AddToCampaignPopover entityType="lead" entityIds={[leadId]} onEnrolled={load} testidPrefix={`drawer-atc-${leadId}`} />
                        <ScaliqPushPopover ids={[leadId]} kind="lead" compact onDone={load} />
                    </div>
                </header>

                <div className="p-5 space-y-6">
                    <section>
                        <div className="grid grid-cols-2 gap-3 text-xs">
                            <Fact label="ICP score" value={lead.score} />
                            <DealValueFact lead={lead} onSaved={() => { load(); onChanged?.(); }} />
                            <Fact label="Email" value={lead.contact_email || "—"} />
                            <Fact label="LinkedIn" value={<LinkedInButton url={lead.contact_linkedin} name={lead.contact_name} variant="inline" testid={`lead-linkedin-${lead.id}`} />} />
                            <Fact label="Stakeholders" value={`${stakeholders.length} mapped`} />
                        </div>
                        {lead.notes && <p className="text-xs text-[var(--jr-text-2)] mt-3 leading-relaxed">{lead.notes}</p>}
                        {lead.scaliq && (
                            <div data-testid={`scaliq-status-${lead.id}`}
                                className="mt-3 rounded-lg border border-[#6366f1]/30 bg-[#6366f1]/8 px-3 py-2.5">
                                <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.16em] text-[#818cf8]">
                                    <Rocket size={11} /> ScalIQ · {lead.scaliq.campaign_name || lead.scaliq.campaign_id}
                                </div>
                                <div className="mt-1.5 flex flex-wrap gap-1.5">
                                    <span className="text-[10px] px-2 py-0.5 rounded-full border border-white/15 text-[var(--jr-text-2)]">
                                        {lead.scaliq.status === "duplicate" ? i18n.t("scaliq.st_duplicate") : i18n.t("scaliq.st_in_campaign")}
                                    </span>
                                    {lead.scaliq.connection_status === "accepted" && (
                                        <span className="text-[10px] px-2 py-0.5 rounded-full border border-[#22c55e]/40 text-[#22c55e]">
                                            {i18n.t("scaliq.st_accepted")}
                                        </span>
                                    )}
                                    {lead.scaliq.replied && (
                                        <span className="text-[10px] px-2 py-0.5 rounded-full border border-[var(--jr-brand)]/50 text-[var(--jr-brand)]">
                                            {i18n.t("scaliq.st_replied")}
                                        </span>
                                    )}
                                    {lead.scaliq.tag && (
                                        <span className="text-[10px] px-2 py-0.5 rounded-full border border-[var(--jr-amber)]/40 text-[var(--jr-amber)]">
                                            {lead.scaliq.tag}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </section>

                    {stakeholders.length > 0 && (
                        <section>
                            <SectionTitle icon={Network}>Stakeholders</SectionTitle>
                            <div className="grid sm:grid-cols-2 gap-2">
                                {stakeholders.map((p, i) => (
                                    <div key={p.id || i}
                                        className="rounded-lg border border-white/10 bg-[var(--jr-surface-2)] p-3 cursor-pointer hover:border-[var(--jr-brand)]/40 transition-colors"
                                        onClick={() => p.id && setContactRef({ kind: "stakeholder", leadId, contactId: p.id })}
                                        data-testid={`drawer-open-stakeholder-${p.id || i}`}>
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <div className="text-sm font-medium text-white truncate">{p.name}</div>
                                                <div className="text-[11px] text-[var(--jr-text-3)] truncate">
                                                    {p.role || "—"}
                                                    <span className="mx-1 opacity-60">·</span>
                                                    <span className="inline-flex items-center gap-1"><Building2 size={9} className="inline" /> {lead.company_name}</span>
                                                </div>
                                            </div>
                                            <span className="text-[9px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded bg-white/5 text-[var(--jr-text-2)]">{p.influence || "neutral"}</span>
                                        </div>
                                        <div className="mt-1.5 flex gap-2 text-[10px]">
                                            <span className={`px-1.5 py-0.5 rounded ${p.sentiment === "warm" ? "bg-[var(--jr-success)]/15 text-[var(--jr-success)]" : p.sentiment === "cold" ? "bg-[var(--jr-info)]/15 text-[var(--jr-info)]" : "bg-white/5 text-[var(--jr-text-2)]"}`}>
                                                {p.sentiment || "neutral"}
                                            </span>
                                            {p.email && <a href={`mailto:${p.email}`} className="text-[var(--jr-brand)] truncate">{p.email}</a>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <Link to="/app/sales/companies" className="mt-3 inline-flex items-center gap-1 text-xs text-[var(--jr-info)] hover:text-white">
                                Bewerk stakeholder map <ArrowRight size={11} />
                            </Link>
                        </section>
                    )}

                    <section>
                        <SectionTitle icon={Send}>{i18n.t("pipeline_sales.outreach_history")} <span className="text-[var(--jr-text-3)] text-[10px] ml-1">({messages.length})</span></SectionTitle>
                        {messages.length === 0 ? (
                            <p className="text-xs text-[var(--jr-text-3)]">{i18n.t("pipeline_sales.no_messages")} <Link to="/app/sales/outreach" className="text-[var(--jr-brand)]">{i18n.t("pipeline_sales.start_outreach")} →</Link></p>
                        ) : (
                            <ul className="space-y-2">
                                {messages.slice(0, 8).map((m) => {
                                    const Icon = CHANNEL_ICONS[m.channel] || Send;
                                    return (
                                        <li key={m.id} className="rounded-lg border border-white/8 bg-[var(--jr-surface-2)] p-3" data-testid={`drawer-msg-${m.id}`}>
                                            <div className="flex items-center justify-between gap-2 mb-1">
                                                <div className="flex items-center gap-2 text-[11px] text-[var(--jr-text-2)]">
                                                    <Icon size={11} /> {m.channel}
                                                    <StatusPill status={m.status} />
                                                </div>
                                                <span className="text-[10px] font-mono text-[var(--jr-text-3)]">{(m.sent_at || m.created_at || "").slice(0, 10)}</span>
                                            </div>
                                            <p className="text-xs text-white line-clamp-2 leading-relaxed">{m.direction === "in" ? (m.reply_snippet || m.body) : (m.body || m.reply_snippet)}</p>
                                            {m.on_behalf_of_user_name && (
                                                <p className="text-[10px] text-[var(--jr-text-3)] mt-1">namens {m.on_behalf_of_user_name}</p>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </section>

                    {audit.length > 0 && (
                        <section>
                            <SectionTitle icon={History}>Activity</SectionTitle>
                            <ul className="space-y-1.5">
                                {audit.map((e) => (
                                    <li key={e.id} className="text-[11px] text-[var(--jr-text-2)] flex gap-3">
                                        <span className="text-[var(--jr-text-3)] font-mono shrink-0 w-32">{(e.created_at || "").replace("T", " ").slice(0, 16)}</span>
                                        <span>{e.detail || e.action}</span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}
                </div>
            </div>
            <ContactDrawer contactRef={contactRef} onClose={() => setContactRef(null)} onChanged={load} />
        </div>
    );
}

function Fact({ label, value }) {
    return (
        <div className="rounded-lg border border-white/8 bg-[var(--jr-surface-2)] px-3 py-2">
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{label}</div>
            <div className="text-sm text-white truncate">{value}</div>
        </div>
    );
}

/* Bewerkbare dealwaarde (EUR) — klik op het potlood, Enter/blur bewaart. */
function DealValueFact({ lead, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [val, setVal] = useState("");
    const [busy, setBusy] = useState(false);
    const start = () => { setVal(lead.deal_value ?? ""); setEditing(true); };
    const save = async () => {
        setBusy(true);
        try {
            await api.put(`/leads/${lead.id}`, { deal_value: val === "" ? 0 : Math.max(0, Number(val) || 0) });
            toast.success(i18n.t("pipeline_sales.value_saved"));
            setEditing(false);
            onSaved?.();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setBusy(false); }
    };
    return (
        <div className="rounded-lg border border-white/8 bg-[var(--jr-surface-2)] px-3 py-2" data-testid={`deal-value-fact-${lead.id}`}>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{i18n.t("pipeline_sales.deal_value")}</div>
            {editing ? (
                <div className="flex items-center gap-1.5 mt-0.5">
                    <input type="number" min="0" step="100" value={val} autoFocus
                        onChange={(e) => setVal(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
                        data-testid={`deal-value-input-${lead.id}`}
                        className="w-24 bg-black/25 border border-white/15 rounded px-2 py-0.5 text-sm text-white outline-none focus:border-[var(--jr-amber)]" />
                    <button onClick={save} disabled={busy} data-testid={`deal-value-save-${lead.id}`}
                        className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--jr-amber)] text-black font-medium disabled:opacity-50">
                        {busy ? "…" : "OK"}
                    </button>
                </div>
            ) : (
                <button onClick={start} data-testid={`deal-value-edit-${lead.id}`}
                    title={i18n.t("pipeline_sales.value_edit_tip")}
                    className="text-sm text-white truncate hover:text-[var(--jr-amber)] inline-flex items-center gap-1.5">
                    <span className={fmtEur(lead.deal_value) ? "text-[var(--jr-amber)] font-medium" : "text-[var(--jr-text-3)]"}>
                        {fmtEur(lead.deal_value) || i18n.t("pipeline_sales.value_none")}
                    </span>
                    <Edit3 size={10} className="text-[var(--jr-text-3)]" />
                </button>
            )}
        </div>
    );
}

function SectionTitle({ icon: Icon, children }) {
    return (
        <h3 className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--jr-text-3)] mb-2 flex items-center gap-1.5">
            {Icon && <Icon size={11} />} {children}
        </h3>
    );
}

function StatusPill({ status }) {
    const MAP = {
        draft:            { bg: "bg-white/5 text-[var(--jr-text-2)]",            label: "draft" },
        pending_approval: { bg: "bg-[var(--jr-amber)]/15 text-[var(--jr-amber)]", label: "pending" },
        approved:         { bg: "bg-[var(--jr-info)]/15 text-[var(--jr-info)]",   label: "approved" },
        sent:             { bg: "bg-[var(--jr-success)]/15 text-[var(--jr-success)]", label: "sent" },
        rejected:         { bg: "bg-[var(--jr-error)]/15 text-[var(--jr-error)]", label: "rejected" },
        replied:          { bg: "bg-[var(--jr-brand)]/15 text-[var(--jr-brand)]", label: "replied" },
    };
    const m = MAP[status] || MAP.draft;
    return <span className={`text-[9px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded ${m.bg}`}>{m.label}</span>;
}

export default LeadDetailDrawer;
